import { FlexBox, InputText, Spinner, Text } from '@dixa/lib-shared-habla';
import cx from 'classnames';

import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';

import { elevioActions } from '../../elevio.actions';
import { elevioSelectors } from '../../elevio.reducer/selectors';
import { Article } from '../Article';
import { KnowledgeErrorPlaceholder } from '../KnowledgeErrorPlaceholder';
import {
  SuggestionList,
  SuggestionsNotEnabledPlaceholder,
} from '../SuggestionList';

import styles from './KnowledgeContent.module.scss';

export function KnowledgeContent(): JSX.Element {
  const pageUrl = window.location.href;
  const dispatch = useAppDispatch();
  const articleId = useAppSelector(elevioSelectors.articleId);
  const token = useAppSelector(elevioSelectors.suggestion.token);
  const authState = useAppSelector(elevioSelectors.suggestion.authState);
  const searchInputValue = useAppSelector(
    elevioSelectors.search.searchInputValue,
  );
  const conversationId = useAppSelector(elevioSelectors.conversation.id);
  const errorMessage = useAppSelector(elevioSelectors.suggestion.errorMessage);
  const isError = !!errorMessage;
  const suggestions = useAppSelector(
    elevioSelectors.suggestion.suggestionsForCurrentConversation,
  );
  const isFetchingSuggestions = useAppSelector(
    elevioSelectors.suggestion.isFetching,
  );
  const languageId = useAppSelector(
    elevioSelectors.suggestion.selectedLanguageId,
  );

  function setSearchInputValue(value: string) {
    if (!conversationId) return;
    dispatch(
      elevioActions.search.setSearchInputValue({ value, conversationId }),
    );
  }

  if (authState === 'pending') {
    return (
      <FlexBox className={styles.spinner} justifyContent="center">
        <Text.Paragraph variant="3" subdued={true}>
          Authenticating...
        </Text.Paragraph>
      </FlexBox>
    );
  }

  if (authState === 'uninitialized') {
    return (
      <FlexBox className={styles.spinner} justifyContent="center">
        <Spinner />
      </FlexBox>
    );
  }

  if (authState === 'rejected') {
    return <SuggestionsNotEnabledPlaceholder />;
  }

  if (errorMessage || !token || authState !== 'fulfilled') {
    return <KnowledgeErrorPlaceholder message={errorMessage} />;
  }

  if (articleId) {
    return <Article id={articleId} languageId={languageId} pageUrl={pageUrl} />;
  }

  const searchInputStyles = cx(styles.searchInput, {
    [styles.searchInputFetching]: isFetchingSuggestions,
  });

  return (
    <>
      <InputText
        className={searchInputStyles}
        onChange={(e) => setSearchInputValue(e.currentTarget.value)}
        value={searchInputValue}
        placeholder="Search for help..."
        icon="search"
      />
      <SuggestionList
        suggestionData={suggestions}
        isError={isError}
        isFetching={isFetchingSuggestions}
      />
    </>
  );
}

